import { useState } from 'react'
import VoiceTextarea from '@/components/VoiceTextarea'
import {
  Layout, Menu, Typography, Table, Tag, Button, Space, Drawer, Modal,
  Form, Input, InputNumber, Select, DatePicker, Card, Row, Col, Badge,
  Statistic, Tooltip, Popconfirm, message, Alert, theme,
} from 'antd'
import {
  PlusOutlined, EditOutlined, QrcodeOutlined,
  BellOutlined, CheckOutlined, AppstoreOutlined, CalendarOutlined,
  ToolOutlined, BarChartOutlined, ReloadOutlined, UserAddOutlined, CarOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts'
import {
  useSetPneumatici, useCreateSet, useUpdateSet, usePatchSetStato, useDeletePrenotazione,
  usePrenotazioniCambio, useCreatePrenotazione,
  useChiudiOrdineLavoro, useCreateOrdineLavoro, useOrdiniLavoro,
  useGeneraReminder, useInviaReminder,
  useTireServiceDashboard, useTireServiceReportOccupazione,
} from '@/hooks/useTireService'
import { useCustomers } from '@/hooks/useCustomers'
import { useVehicles } from '@/hooks/useVehicles'
import CustomerFormModal from '@/components/CustomerFormModal'
import VehicleFormModal from '@/components/VehicleFormModal'
import type { SetPneumatici, PrenotazioneCambio, OrdineLavoroCambio, StatoSetType, StagioneType, StatoPrenotazioneType } from '@/types/tireService'
import { API_ENDPOINTS } from '@/config/api'
import axiosInstance from '@/lib/axios'

const { Sider, Content } = Layout
const { Title, Text } = Typography

const STAGIONE_COLORS: Record<string, string> = {
  INVERNALE: 'blue',
  ESTIVA: 'orange',
  '4STAGIONI': 'green',
}

const STATO_SET_COLORS: Record<string, string> = {
  STOCCATO: 'default',
  MONTATO: 'processing',
  DISMESSO: 'error',
}

const STATO_PREN_COLORS: Record<string, string> = {
  ATTESA: 'warning',
  CONFERMATA: 'success',
  IN_LAVORAZIONE: 'processing',
  COMPLETATA: 'default',
  ANNULLATA: 'error',
}

type Section = 'magazzino' | 'prenotazioni' | 'ordini' | 'report'

// ── Sezione Magazzino ────────────────────────────────────────────────────────

function MagazzinoSection() {
  const [pageSize, setPageSize] = useState(10)
  const [filters, setFilters] = useState<{ stato?: StatoSetType; stagione?: StagioneType; misura?: string }>({})
  const [selectedSet, setSelectedSet] = useState<SetPneumatici | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingSet, setEditingSet] = useState<SetPneumatici | null>(null)
  const [form] = Form.useForm()

  const { data: sets = [], isLoading, refetch } = useSetPneumatici(filters)
  const createSet = useCreateSet()
  const updateSet = useUpdateSet()
  const patchStato = usePatchSetStato()

  const handleOpenNew = () => {
    setEditingSet(null)
    form.resetFields()
    setModalOpen(true)
  }
  const handleOpenEdit = (s: SetPneumatici) => {
    setEditingSet(s)
    form.setFieldsValue({
      vehicle_id: s.vehicle_id,
      misura: s.misura,
      marca: s.marca,
      stagione: s.stagione,
      quantita: s.quantita,
      profondita_mm: s.profondita_mm,
      dot_anno: s.dot_anno,
      posizione: s.posizione,
    })
    setModalOpen(true)
  }

  const handleSubmit = async (values: any) => {
    if (editingSet) {
      await updateSet.mutateAsync({ id: editingSet.id, data: values })
    } else {
      await createSet.mutateAsync(values)
    }
    setModalOpen(false)
    form.resetFields()
  }

  const handlePrintQr = (s: SetPneumatici) => {
    const url = `${axiosInstance.defaults.baseURL}${API_ENDPOINTS.TIRE_SERVICE.SET_QR_LABEL(s.id)}`
    window.open(url, '_blank')
  }

  const columns = [
    { title: 'Codice', dataIndex: 'codice', key: 'codice', render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    {
      title: 'Veicolo', key: 'vehicle',
      render: (_: any, r: SetPneumatici) => r.vehicle ? <Text><strong>{r.vehicle.targa}</strong><br /><small>{r.vehicle.marca} {r.vehicle.modello}</small></Text> : '—',
    },
    { title: 'Misura', dataIndex: 'misura', key: 'misura' },
    {
      title: 'Stagione', dataIndex: 'stagione', key: 'stagione',
      render: (v: string) => <Tag color={STAGIONE_COLORS[v]}>{v}</Tag>,
    },
    { title: 'Posizione', dataIndex: 'posizione', key: 'posizione', render: (v: string) => v || '—' },
    { title: 'Qtà', dataIndex: 'quantita', key: 'quantita', width: 60 },
    {
      title: 'Stato', dataIndex: 'stato', key: 'stato',
      render: (v: string) => <Badge status={STATO_SET_COLORS[v] as any} text={v} />,
    },
    {
      title: 'Azioni', key: 'actions',
      render: (_: any, r: SetPneumatici) => (
        <Space>
          <Tooltip title="Dettaglio"><Button size="small" icon={<AppstoreOutlined />} onClick={() => { setSelectedSet(r); setDrawerOpen(true) }} /></Tooltip>
          <Tooltip title="Stampa QR"><Button size="small" icon={<QrcodeOutlined />} onClick={() => handlePrintQr(r)} /></Tooltip>
          <Tooltip title="Modifica"><Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(r)} /></Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Select allowClear placeholder="Stato" style={{ width: 130 }} onChange={v => setFilters(f => ({ ...f, stato: v }))} options={['STOCCATO', 'MONTATO', 'DISMESSO'].map(v => ({ label: v, value: v }))} />
            <Select allowClear placeholder="Stagione" style={{ width: 140 }} onChange={v => setFilters(f => ({ ...f, stagione: v }))} options={['INVERNALE', 'ESTIVA', '4STAGIONI'].map(v => ({ label: v, value: v }))} />
            <Input.Search placeholder="Misura (es. 205/55 R16)" style={{ width: 200 }} onSearch={v => setFilters(f => ({ ...f, misura: v || undefined }))} allowClear />
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Aggiorna</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenNew}>Nuovo Set</Button>
          </Space>
        </Col>
      </Row>

      <Table
        dataSource={sets}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: (_: number, size: number) => setPageSize(size),
          showTotal: (total: number) => `Totale ${total} set`,
        }}
        onRow={r => ({ onDoubleClick: () => { setSelectedSet(r); setDrawerOpen(true) } })}
      />

      <Drawer
        title={selectedSet ? `Set ${selectedSet.codice}` : 'Dettaglio Set'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        extra={selectedSet && <Button icon={<QrcodeOutlined />} onClick={() => handlePrintQr(selectedSet!)}>Stampa QR</Button>}
      >
        {selectedSet && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card size="small">
              <Row gutter={8}>
                <Col span={12}><Statistic title="Misura" value={selectedSet.misura} /></Col>
                <Col span={12}><Statistic title="Stagione" value={selectedSet.stagione} /></Col>
                <Col span={12}><Statistic title="Quantità" value={selectedSet.quantita} /></Col>
                <Col span={12}><Statistic title="Profondità" value={selectedSet.profondita_mm ? `${selectedSet.profondita_mm} mm` : '—'} /></Col>
              </Row>
            </Card>
            {selectedSet.posizione && <Alert message={`Posizione scaffale: ${selectedSet.posizione}`} type="info" showIcon />}
            <Space>
              {(['STOCCATO', 'MONTATO', 'DISMESSO'] as StatoSetType[]).map(s => (
                <Button key={s} size="small"
                  type={selectedSet.stato === s ? 'primary' : 'default'}
                  onClick={() => patchStato.mutate({ id: selectedSet.id, stato: s })}
                >{s}</Button>
              ))}
            </Space>
          </Space>
        )}
      </Drawer>

      <Modal
        title={editingSet ? 'Modifica Set' : 'Nuovo Set Pneumatici'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createSet.isPending || updateSet.isPending}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="vehicle_id" label="Vehicle ID" rules={[{ required: !editingSet }]}>
            <InputNumber style={{ width: '100%' }} placeholder="ID del veicolo" />
          </Form.Item>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="misura" label="Misura" rules={[{ required: true }]}><Input placeholder="es. 205/55 R16" /></Form.Item></Col>
            <Col span={12}><Form.Item name="marca" label="Marca"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="stagione" label="Stagione" rules={[{ required: true }]}>
                <Select options={['INVERNALE', 'ESTIVA', '4STAGIONI'].map(v => ({ label: v, value: v }))} />
              </Form.Item>
            </Col>
            <Col span={12}><Form.Item name="quantita" label="Quantità" initialValue={4}><InputNumber min={1} max={20} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="profondita_mm" label="Profondità (mm)"><InputNumber min={0} step={0.5} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="dot_anno" label="Anno DOT"><InputNumber min={1990} max={2099} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="posizione" label="Posizione scaffale"><Input placeholder="es. A-03" /></Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ── Sezione Prenotazioni ─────────────────────────────────────────────────────

function PrenotazioniSection({ onOrdineCreato }: { onOrdineCreato: () => void }) {
  const [pageSize, setPageSize] = useState(10)
  const [filters, setFilters] = useState<{ stato?: StatoPrenotazioneType }>({})
  const [modalOpen, setModalOpen] = useState(false)
  const [qrModal, setQrModal] = useState<{ open: boolean; id?: number; codice?: string; blobUrl?: string }>({ open: false })
  const [reminderModal, setReminderModal] = useState<{ open: boolean; id?: number; testo?: string }>({ open: false })
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false)
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>()
  const [pendingVehicles, setPendingVehicles] = useState<any[]>([])
  const [form] = Form.useForm()

  const { data: prenotazioni = [], isLoading, refetch } = usePrenotazioniCambio(filters)
  const { data: customersData, refetch: refetchCustomers } = useCustomers(1, 1000)
  const { data: vehiclesData, refetch: refetchVehicles } = useVehicles(1, 1000)
  const createPren = useCreatePrenotazione()
  const deletePren = useDeletePrenotazione()
  const generaReminder = useGeneraReminder()
  const inviaReminder = useInviaReminder()
  const createOrdine = useCreateOrdineLavoro()

  const customerVehicles = selectedCustomerId
    ? (() => {
        const fromOwnership = vehiclesData?.items.filter((v: any) => v.current_customer_id === selectedCustomerId) ?? []
        const ownedIds = new Set(fromOwnership.map((v: any) => v.id))
        const extras = pendingVehicles.filter(v => !ownedIds.has(v.id))
        return [...fromOwnership, ...extras]
      })()
    : []

  const handleCustomerChange = (customerId: number) => {
    setSelectedCustomerId(customerId)
    setPendingVehicles([])
    form.setFieldValue('vehicle_id', undefined)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    form.resetFields()
    setSelectedCustomerId(undefined)
  }

  const handleGeneraReminder = async (id: number) => {
    const res = await generaReminder.mutateAsync(id)
    setReminderModal({ open: true, id, testo: res.testo })
  }

  const handleInviaReminder = async () => {
    if (!reminderModal.id || !reminderModal.testo) return
    try {
      const res = await inviaReminder.mutateAsync({ id: reminderModal.id, canale: 'whatsapp', testo: reminderModal.testo })
      const canale = (res as any)?.canale_usato?.toUpperCase() ?? 'WHATSAPP/SMS'
      message.success(`Reminder inviato via ${canale}`)
      setReminderModal({ open: false })
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Errore invio reminder')
    }
  }

  const columns = [
    {
      title: 'Cliente', key: 'cliente',
      render: (_: any, r: PrenotazioneCambio) => r.customer ? `${r.customer.nome} ${r.customer.cognome}` : '—',
    },
    {
      title: 'Veicolo', key: 'veicolo',
      render: (_: any, r: PrenotazioneCambio) => r.vehicle?.targa || '—',
    },
    {
      title: 'Data/Ora', dataIndex: 'data_ora', key: 'data_ora',
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm'),
      sorter: (a: PrenotazioneCambio, b: PrenotazioneCambio) => dayjs(a.data_ora).unix() - dayjs(b.data_ora).unix(),
    },
    {
      title: 'Tipo', dataIndex: 'tipo_servizio', key: 'tipo_servizio',
      render: (v: string) => <Tag>{v?.replace(/_/g, ' ') ?? '—'}</Tag>,
    },
    {
      title: 'Stato', dataIndex: 'stato', key: 'stato',
      render: (v: string) => <Badge status={STATO_PREN_COLORS[v] as any} text={v} />,
    },
    {
      title: 'QR', key: 'qr',
      render: (_: any, r: PrenotazioneCambio) => r.qr_usato
        ? <Tag color="default">Usato</Tag>
        : r.qr_attivo
          ? <Tag color="success">Attivo</Tag>
          : <Tag color="error">Non attivo</Tag>,
    },
    {
      title: 'Azioni', key: 'actions',
      render: (_: any, r: PrenotazioneCambio) => (
        <Space>
          {r.stato === 'CONFERMATA' && (
            <Tooltip title="Crea ordine di lavoro">
              <Button size="small" icon={<ToolOutlined />} loading={createOrdine.isPending}
                onClick={async () => {
                  try { await createOrdine.mutateAsync({ prenotazione_id: r.id }); onOrdineCreato() } catch { /* gestito dal hook */ }
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Visualizza QR prenotazione">
            <Button size="small" icon={<QrcodeOutlined />} onClick={async () => {
              const url = API_ENDPOINTS.TIRE_SERVICE.PRENOTAZIONE_QR_IMAGE(r.id)
              const res = await axiosInstance.get(url, { responseType: 'blob' })
              const blobUrl = URL.createObjectURL(res.data)
              setQrModal({ open: true, id: r.id, codice: r.codice, blobUrl })
            }} />
          </Tooltip>
          <Tooltip title="Reminder AI">
            <Button size="small" icon={<BellOutlined />} onClick={() => handleGeneraReminder(r.id)} />
          </Tooltip>
          <Popconfirm title="Annullare la prenotazione?" onConfirm={() => deletePren.mutate(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Select allowClear placeholder="Stato" style={{ width: 160 }} onChange={v => setFilters(f => ({ ...f, stato: v }))}
            options={['ATTESA', 'CONFERMATA', 'IN_LAVORAZIONE', 'COMPLETATA', 'ANNULLATA'].map(v => ({ label: v, value: v }))} />
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Aggiorna</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>Nuova Prenotazione</Button>
          </Space>
        </Col>
      </Row>

      <Table dataSource={prenotazioni} columns={columns} rowKey="id" loading={isLoading} size="small" pagination={{
        pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        onShowSizeChange: (_: number, size: number) => setPageSize(size),
        showTotal: (total: number) => `Totale ${total} prenotazioni`,
      }} />

      <Modal title="Nuova Prenotazione Cambio" open={modalOpen} onCancel={handleModalClose}
        onOk={() => form.submit()} confirmLoading={createPren.isPending} width={560}>
        <Form form={form} layout="vertical" onFinish={async (v) => {
          try {
            await createPren.mutateAsync({
              customer_id: v.customer_id,
              vehicle_id: v.vehicle_id,
              data_ora: v.data_ora.toISOString(),
              tipo_servizio: v.tipo_servizio,
              note: v.note,
            })
            handleModalClose()
          } catch {
            message.error('Errore nella creazione della prenotazione')
          }
        }}>
          <Form.Item label="Cliente" required style={{ marginBottom: 8 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="customer_id" noStyle rules={[{ required: true, message: 'Seleziona un cliente' }]}>
                <Select showSearch placeholder="Cerca cliente per nome…" optionFilterProp="label"
                  onChange={handleCustomerChange}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={customersData?.items.map((c: any) => ({
                    value: c.id,
                    label: c.tipo === 'azienda' && c.ragione_sociale ? c.ragione_sociale : `${c.nome || ''} ${c.cognome || ''}`.trim(),
                  }))}
                  style={{ flex: 1 }}
                />
              </Form.Item>
              <Button icon={<UserAddOutlined />} onClick={() => setIsCustomerModalOpen(true)} title="Nuovo cliente" />
            </Space.Compact>
          </Form.Item>

          <Form.Item label="Veicolo" required style={{ marginBottom: 8 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="vehicle_id" noStyle rules={[{ required: true, message: 'Seleziona un veicolo' }]}>
                <Select showSearch placeholder={selectedCustomerId ? 'Seleziona veicolo' : 'Prima seleziona il cliente'}
                  disabled={!selectedCustomerId} optionFilterProp="label"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={customerVehicles?.map((v: any) => ({ value: v.id, label: `${v.targa} — ${v.marca} ${v.modello}` }))}
                  style={{ flex: 1 }}
                />
              </Form.Item>
              <Button icon={<CarOutlined />} disabled={!selectedCustomerId} onClick={() => setIsVehicleModalOpen(true)} title="Nuovo veicolo" />
            </Space.Compact>
          </Form.Item>

          <Form.Item name="data_ora" label="Data e Ora" rules={[{ required: true }]}>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="tipo_servizio" label="Tipo Servizio" rules={[{ required: true }]}>
            <Select options={['CAMBIO', 'SOLO_DEPOSITO', 'CAMBIO_E_DEPOSITO'].map(v => ({ label: v.replace(/_/g, ' '), value: v }))} />
          </Form.Item>
          <Form.Item name="note" label="Note"><VoiceTextarea rows={2} minHeight={70} maxHeight={150} label="Note" compact /></Form.Item>
        </Form>
      </Modal>

      <CustomerFormModal open={isCustomerModalOpen} onCancel={() => setIsCustomerModalOpen(false)}
        onSuccess={(newCustomer: any) => {
          setIsCustomerModalOpen(false)
          refetchCustomers()
          setSelectedCustomerId(newCustomer.id)
          form.setFieldsValue({ customer_id: newCustomer.id, vehicle_id: undefined })
        }}
      />

      <VehicleFormModal open={isVehicleModalOpen} onCancel={() => setIsVehicleModalOpen(false)}
        onSuccess={(newVehicle: any) => {
          setIsVehicleModalOpen(false)
          setPendingVehicles(prev => [...prev, newVehicle])
          refetchVehicles()
          form.setFieldValue('vehicle_id', newVehicle.id)
        }}
      />

      <Modal title="Reminder AI" open={reminderModal.open}
        onCancel={() => setReminderModal({ open: false })}
        onOk={handleInviaReminder} okText="Invia WhatsApp / SMS"
        confirmLoading={inviaReminder.isPending}>
        <Input.TextArea rows={4} value={reminderModal.testo}
          onChange={e => setReminderModal(s => ({ ...s, testo: e.target.value }))} />
        <Text type="secondary">{reminderModal.testo?.length || 0} / 160 caratteri</Text>
      </Modal>

      <Modal
        title={`QR Prenotazione — ${qrModal.codice ?? ''}`}
        open={qrModal.open}
        onCancel={() => { URL.revokeObjectURL(qrModal.blobUrl ?? ''); setQrModal({ open: false }) }}
        footer={null}
        centered
      >
        {qrModal.blobUrl && (
          <Space direction="vertical" style={{ width: '100%', alignItems: 'center' }} size="large">
            <img
              src={qrModal.blobUrl}
              alt="QR Prenotazione"
              style={{ maxWidth: 280, border: '1px solid #d9d9d9', borderRadius: 8 }}
            />
            <Space>
              <Button
                icon={<QrcodeOutlined />}
                onClick={() => {
                  const a = document.createElement('a')
                  a.href = qrModal.blobUrl!
                  a.download = `qr-prenotazione-${qrModal.codice}.png`
                  a.click()
                }}
              >
                Scarica PNG
              </Button>
              <Button
                type="primary"
                icon={<BellOutlined />}
                loading={generaReminder.isPending}
                onClick={async () => {
                  const id = qrModal.id
                  URL.revokeObjectURL(qrModal.blobUrl ?? '')
                  setQrModal({ open: false })
                  if (id) await handleGeneraReminder(id)
                }}
              >
                Invia WhatsApp
              </Button>
            </Space>
          </Space>
        )}
      </Modal>
    </>
  )
}

// ── Sezione Ordini Lavoro ─────────────────────────────────────────────────────

function OrdiniSection() {
  const [pageSize, setPageSize] = useState(10)
  const { data: ordini = [], isLoading } = useOrdiniLavoro()
  const chiudi = useChiudiOrdineLavoro()
  const inviaReminder = useInviaReminder()

  const [notificaModal, setNotificaModal] = useState<{
    open: boolean
    ordine: OrdineLavoroCambio | null
    testo: string
    loading: boolean
    sending: boolean
  }>({ open: false, ordine: null, testo: '', loading: false, sending: false })

  const handleApriNotifica = async (r: OrdineLavoroCambio) => {
    setNotificaModal({ open: true, ordine: r, testo: '', loading: true, sending: false })
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.TIRE_SERVICE.ORDINE_TESTO_NOTIFICA(r.id))
      setNotificaModal(s => ({ ...s, testo: res.data.testo, loading: false }))
    } catch {
      setNotificaModal(s => ({ ...s, testo: 'Il suo veicolo è pronto con i nuovi pneumatici. Può venire a ritirarlo. Grazie!', loading: false }))
    }
  }

  const handleChiudiSoloOrdine = async () => {
    if (!notificaModal.ordine) return
    await chiudi.mutateAsync(notificaModal.ordine.id)
    setNotificaModal({ open: false, ordine: null, testo: '', loading: false, sending: false })
  }

  const handleInviaEChiudi = async () => {
    if (!notificaModal.ordine) return
    const pren = notificaModal.ordine.prenotazione
    if (!pren) return
    setNotificaModal(s => ({ ...s, sending: true }))
    try {
      await inviaReminder.mutateAsync({ id: pren.id, canale: 'whatsapp', testo: notificaModal.testo })
      const canale = 'WhatsApp/SMS'
      message.success(`Notifica inviata via ${canale}`)
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Errore invio notifica')
      setNotificaModal(s => ({ ...s, sending: false }))
      return
    }
    await chiudi.mutateAsync(notificaModal.ordine.id)
    setNotificaModal({ open: false, ordine: null, testo: '', loading: false, sending: false })
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Cliente', key: 'cliente',
      render: (_: any, r: OrdineLavoroCambio) => r.prenotazione?.customer
        ? `${r.prenotazione.customer.nome} ${r.prenotazione.customer.cognome}` : '—',
    },
    { title: 'Veicolo', key: 'veicolo', render: (_: any, r: OrdineLavoroCambio) => r.prenotazione?.vehicle?.targa || '—' },
    { title: 'Tipo', key: 'tipo', render: (_: any, r: OrdineLavoroCambio) => r.prenotazione?.tipo_servizio?.replace(/_/g, ' ') ?? '—' },
    { title: 'Inizio', dataIndex: 'inizio_at', key: 'inizio_at', render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—' },
    {
      title: 'Fine', dataIndex: 'fine_at', key: 'fine_at',
      render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Tag color="processing">In corso</Tag>,
    },
    { title: 'Totale', dataIndex: 'totale', key: 'totale', render: (v: any) => v != null ? `€ ${Number(v).toFixed(2)}` : '—' },
    {
      title: 'Azioni', key: 'actions',
      render: (_: any, r: OrdineLavoroCambio) => !r.fine_at ? (
        <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleApriNotifica(r)}>
          Chiudi
        </Button>
      ) : <Tag color="success">Completato</Tag>,
    },
  ]

  return (
    <>
      <Table dataSource={ordini} columns={columns} rowKey="id" loading={isLoading} size="small" pagination={{
        pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        onShowSizeChange: (_: number, size: number) => setPageSize(size),
        showTotal: (total: number) => `Totale ${total} ordini`,
      }} />

      <Modal
        title="Notifica cliente — veicolo pronto"
        open={notificaModal.open}
        onCancel={() => setNotificaModal({ open: false, ordine: null, testo: '', loading: false, sending: false })}
        footer={[
          <Button key="annulla" onClick={() => setNotificaModal({ open: false, ordine: null, testo: '', loading: false, sending: false })}>
            Annulla
          </Button>,
          <Button key="chiudi" onClick={handleChiudiSoloOrdine} loading={chiudi.isPending && !notificaModal.sending}>
            Chiudi senza inviare
          </Button>,
          <Button key="invia" type="primary" icon={<BellOutlined />} onClick={handleInviaEChiudi}
            loading={notificaModal.sending} disabled={notificaModal.loading || !notificaModal.testo.trim()}>
            Invia e chiudi
          </Button>,
        ]}
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Text type="secondary">
            Modifica il messaggio se necessario, poi premi <strong>Invia e chiudi</strong> per notificare il cliente via WhatsApp/SMS e chiudere l'ordine.
          </Text>
          <Input.TextArea
            rows={4}
            value={notificaModal.testo}
            onChange={e => setNotificaModal(s => ({ ...s, testo: e.target.value }))}
            disabled={notificaModal.loading || notificaModal.sending}
            placeholder="Caricamento messaggio..."
          />
          <Text type="secondary" style={{ fontSize: 11 }}>{notificaModal.testo.length} / 160 caratteri</Text>
        </Space>
      </Modal>
    </>
  )
}

// ── Sezione Report ───────────────────────────────────────────────────────────

function ReportSection() {
  const { data: dashboard } = useTireServiceDashboard()
  const { data: occupazione } = useTireServiceReportOccupazione()

  const chartData = occupazione?.items.map(i => ({ name: `${i.misura} ${i.stagione}`, quantita: i.quantita })) || []

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Row gutter={16}>
        {[
          { title: 'Set in deposito', value: dashboard?.set_stoccati ?? '—' },
          { title: 'Prenotazioni oggi', value: dashboard?.prenotazioni_oggi ?? '—' },
          { title: 'Ordini aperti', value: dashboard?.ordini_aperti ?? '—' },
          { title: 'Ricavi settimana', value: dashboard?.ricavi_settimana != null ? `€ ${Number(dashboard.ricavi_settimana).toFixed(2)}` : '—' },
          { title: 'Da contattare', value: dashboard?.clienti_da_contattare ?? '—' },
        ].map(kpi => (
          <Col key={kpi.title} xs={12} sm={8} md={4}>
            <Card size="small"><Statistic title={kpi.title} value={kpi.value} /></Card>
          </Col>
        ))}
      </Row>

      <Card title="Occupazione magazzino per misura/stagione" size="small">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
            <YAxis />
            <RTooltip />
            <Bar dataKey="quantita" fill="#1677ff" name="Quantità" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </Space>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function TireServicePage() {
  const { token } = theme.useToken()
  const [section, setSection] = useState<Section>('magazzino')

  const menuItems = [
    { key: 'magazzino', icon: <AppstoreOutlined />, label: 'Magazzino' },
    { key: 'prenotazioni', icon: <CalendarOutlined />, label: 'Prenotazioni' },
    { key: 'ordini', icon: <ToolOutlined />, label: 'Ordini lavoro' },
    { key: 'report', icon: <BarChartOutlined />, label: 'Report' },
  ]

  return (
    <Layout style={{ minHeight: '100%', background: 'transparent' }}>
      <Sider width={200} style={{ background: token.colorBgContainer, borderRight: `1px solid ${token.colorBorderSecondary}` }}>
        <div style={{ padding: '16px 16px 8px', fontWeight: 600, fontSize: 13, color: token.colorTextSecondary }}>PNEUMATICI</div>
        <Menu mode="inline" selectedKeys={[section]} items={menuItems}
          onClick={({ key }) => setSection(key as Section)} style={{ border: 'none' }} />
      </Sider>
      <Content style={{ padding: 24, background: token.colorBgLayout }}>
        <Title level={4} style={{ marginTop: 0 }}>{menuItems.find(m => m.key === section)?.label}</Title>
        {section === 'magazzino' && <MagazzinoSection />}
        {section === 'prenotazioni' && <PrenotazioniSection onOrdineCreato={() => setSection('ordini')} />}
        {section === 'ordini' && <OrdiniSection />}
        {section === 'report' && <ReportSection />}
      </Content>
    </Layout>
  )
}
